import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Wallet, Target, TrendingUp, Activity, Zap, Bot,
  ChevronRight, ArrowUpRight, ArrowDownRight, Clock, Gift
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

function StatCard({ icon: Icon, label, value, sub, color = 'blue', delay = 0 }) {
  const colors = {
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-500/20 text-blue-400',
    lime: 'from-lime-500/20 to-lime-500/5 border-lime-400/20 text-lime-400',
    pink: 'from-pink-500/20 to-pink-500/5 border-pink-400/20 text-pink-400',
    slate: 'from-slate-500/20 to-slate-500/5 border-slate-400/20 text-slate-400',
  };
  return (
    <motion.div {...fadeUp} transition={{ delay: delay * 0.1, duration: 0.4 }}
      className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 relative overflow-hidden group hover:scale-[1.02] transition-transform`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="opacity-70" />
        <span className="text-xs uppercase tracking-wider opacity-70 font-manrope">{label}</span>
      </div>
      <p className="font-barlow text-2xl md:text-3xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-60 font-manrope">{sub}</p>}
    </motion.div>
  );
}

function ReliabilityRing({ score }) {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#bef264' : score >= 50 ? '#3B82F6' : '#ec4899';

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
        <circle cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-barlow text-3xl font-bold text-white">{score}</span>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">Trust</span>
      </div>
    </div>
  );
}

export default function PlayerDashboard() {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [nudges, setNudges] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, nudgeRes] = await Promise.all([
        api.get('/dashboard/player'),
        api.get('/agents/personalize'),
      ]);
      setDashboard(dashRes.data);
      setNudges(nudgeRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard;
  const w = d?.wallet || {};
  const u = d?.user || {};

  return (
    <div data-testid="player-dashboard" className="space-y-6">
      {/* Header */}
      <motion.div {...fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight">
            Hey, {u.name || user?.name || 'Player'}
          </h1>
          <p className="text-slate-400 text-sm font-manrope mt-0.5">Your rewards dashboard</p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-lime-400/10 border border-lime-400/20">
          <div className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse" />
          <span className="text-xs text-lime-400 font-mono uppercase">Live</span>
        </div>
      </motion.div>

      {/* Bento Grid: Hero Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Balance" value={`${w.balance || 0} $PLAY`} sub="Available" color="blue" delay={0} />
        <StatCard icon={Target} label="Staked" value={`${w.staked || 0} $PLAY`} sub="Locked in sessions" color="lime" delay={1} />
        <StatCard icon={TrendingUp} label="Earned" value={`${w.lifetime_earned || 0}`} sub="Lifetime rewards" color="blue" delay={2} />
        <StatCard icon={Activity} label="Streak" value={`${u.streak || 0}`} sub={`${u.attendance_rate || 0}% attendance`} color="lime" delay={3} />
      </div>

      {/* Middle Row: Reliability + Nudges */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Reliability Score */}
        <motion.div {...fadeUp} transition={{ delay: 0.2 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5 md:col-span-1"
        >
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-4">
            Reliability Score
          </h3>
          <ReliabilityRing score={u.trust_score || 50} />
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-xs font-manrope">
              <span className="text-slate-400">Attendance</span>
              <span className="text-white">{u.attendance_rate || 0}%</span>
            </div>
            <div className="flex justify-between text-xs font-manrope">
              <span className="text-slate-400">Sessions Played</span>
              <span className="text-white">{u.total_sessions || 0}</span>
            </div>
            <div className="flex justify-between text-xs font-manrope">
              <span className="text-slate-400">No-Shows (30d)</span>
              <span className={`${(u.recent_no_shows || 0) > 0 ? 'text-pink-400' : 'text-lime-400'}`}>
                {u.recent_no_shows || 0}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Agent Nudges */}
        <motion.div {...fadeUp} transition={{ delay: 0.3 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5 md:col-span-2"
          data-testid="agent-nudges"
        >
          <div className="flex items-center gap-2 mb-4">
            <Bot size={16} className="text-blue-400" />
            <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">AI Coach</h3>
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          </div>

          {nudges?.motivation_message && (
            <p className="text-slate-300 text-sm mb-4 font-manrope italic border-l-2 border-blue-500/40 pl-3">
              {nudges.motivation_message}
            </p>
          )}

          <div className="space-y-3">
            {(nudges?.nudges || []).slice(0, 3).map((nudge, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/8 transition-colors group cursor-pointer">
                <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  nudge.type === 'streak' ? 'bg-lime-500/20 text-lime-400' :
                  nudge.type === 'off_peak' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-pink-500/20 text-pink-400'
                }`}>
                  {nudge.type === 'streak' ? <Zap size={16} /> :
                   nudge.type === 'off_peak' ? <Clock size={16} /> :
                   <Gift size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{nudge.title}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{nudge.message}</p>
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-300 transition-colors mt-1" />
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Active Rewards */}
      {d?.active_rewards?.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.4 }}>
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Gift size={16} className="text-lime-400" />
            Active Reward Offers
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.active_rewards.slice(0, 6).map((reward, i) => (
              <div key={reward.id || i}
                className="bg-slate-950/60 backdrop-blur-xl border border-lime-400/10 rounded-xl p-4 hover:border-lime-400/30 transition-all group cursor-pointer"
                data-testid={`reward-card-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                    reward.urgency === 'critical' ? 'bg-pink-500/20 text-pink-400' :
                    reward.urgency === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>{reward.urgency}</span>
                  <span className="text-xs text-slate-500">{reward.target_segment}</span>
                </div>
                <p className="font-barlow text-xl font-bold text-lime-400">+{reward.reward_amount} $PLAY</p>
                <p className="text-slate-400 text-xs mt-1 font-manrope">{reward.explanation}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Transactions */}
      <motion.div {...fadeUp} transition={{ delay: 0.5 }}>
        <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
          <Activity size={16} className="text-blue-400" />
          Recent Activity
        </h3>
        <div className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden" data-testid="transaction-list">
          {(d?.recent_transactions || []).slice(0, 8).map((tx, i) => (
            <div key={tx.id || i} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                tx.amount > 0 ? 'bg-lime-500/20' : 'bg-pink-500/20'
              }`}>
                {tx.amount > 0 ? <ArrowUpRight size={16} className="text-lime-400" /> : <ArrowDownRight size={16} className="text-pink-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{tx.description}</p>
                <p className="text-slate-500 text-xs font-mono">{new Date(tx.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`font-barlow font-bold text-sm ${tx.amount > 0 ? 'text-lime-400' : 'text-pink-400'}`}>
                {tx.amount > 0 ? '+' : ''}{tx.amount}
              </span>
            </div>
          ))}
          {(!d?.recent_transactions || d.recent_transactions.length === 0) && (
            <div className="p-8 text-center text-slate-500 text-sm">No transactions yet</div>
          )}
        </div>
      </motion.div>

      {/* Recent AI Decisions */}
      {d?.recent_decisions?.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.6 }}>
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Bot size={16} className="text-blue-400" />
            Recent Agent Decisions
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          </h3>
          <div className="space-y-2">
            {d.recent_decisions.map((dec, i) => (
              <div key={dec.id || i}
                className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-blue-500/20 transition-all"
                data-testid={`decision-card-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                    dec.decision_type === 'STAKE_QUOTE' ? 'bg-blue-500/20 text-blue-400' :
                    dec.decision_type === 'REWARD_OFFER' ? 'bg-lime-500/20 text-lime-400' :
                    dec.decision_type === 'SUPPRESS' ? 'bg-pink-500/20 text-pink-400' :
                    'bg-orange-500/20 text-orange-400'
                  }`}>{dec.decision_type?.replace('_', ' ')}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    dec.status === 'executed' ? 'bg-lime-500/10 text-lime-400' :
                    dec.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                    'bg-slate-500/10 text-slate-400'
                  }`}>{dec.status}</span>
                  <span className="text-xs text-slate-500 ml-auto font-mono">
                    {new Date(dec.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-300 text-sm">{dec.explanation_user}</p>
                {dec.reason_codes?.length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {dec.reason_codes.map((rc, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400 font-mono">{rc}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
